/**
 * "Lög æfingarinnar" editor (editors: the conductor, admins): the program's items in order — single
 * songs and whole folders / setlists — moved up / down or removed, and below them a search over the
 * library's setlists, folders and songs to add more (each once). "Vista" sends the whole program
 * (PUT /events/:id/program); a folder / setlist stays linked, so its later changes show in the
 * practice at once. Nothing is saved until "Vista"; "Hætta við" drops the changes.
 */
import { ArrowDown, ArrowUp, Check, Folder, ListOrdered, Music, Plus, X, type LucideIcon } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import type { EventDTO } from '../../shared/dto/events';
import type { SongListItemDTO } from '../../shared/dto/library';
import { uuid } from '../../shared/ids';
import { MAX_PROGRAM_ITEMS, type ProgramItem } from '../../shared/program';
import { useCollections, useSongs } from '../api/library';
import { useSetProgram } from '../api/practice';
import { useToast } from '../components/toast';
import { Button, cx, ErrorBox, Sheet } from '../components/ui';
import { useT } from '../i18n';
import { errorText } from '../screens/calendar/shared';
import { fold, queryTokens } from '../screens/library/fold';
import { creditsLine } from '../screens/library/format';
import { SearchField } from '../screens/library/SearchField';
import { addCollection, addSong, moveItem, removeItem, sameProgram } from './model';

export function ProgramEditor({ ev, open, onClose }: { ev: EventDTO; open: boolean; onClose: () => void }) {
  // Mounted only while open: every opening starts from the saved program.
  return open ? <EditorSheet ev={ev} onClose={onClose} /> : null;
}

const collator = new Intl.Collator('is');

function EditorSheet({ ev, onClose }: { ev: EventDTO; onClose: () => void }) {
  const t = useT();
  const toast = useToast();
  const songsQ = useSongs();
  const colsQ = useCollections();
  const save = useSetProgram(ev.id);
  const saved = useMemo(() => (Array.isArray(ev.program) ? ev.program : []), [ev.program]);
  const [items, setItems] = useState<ProgramItem[]>(saved);
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  const songs = useMemo(() => new Map((songsQ.data ?? []).map((s) => [s.id, s])), [songsQ.data]);
  const collections = useMemo(() => new Map((colsQ.data ?? []).map((c) => [c.id, c])), [colsQ.data]);
  const full = items.length >= MAX_PROGRAM_ITEMS;

  const results = useMemo(() => {
    const tokens = queryTokens(query);
    const match = (...parts: (string | null)[]) => {
      const hay = fold(parts.filter(Boolean).join(' '));
      return tokens.every((tok) => hay.includes(tok));
    };
    const cols = [...(colsQ.data ?? [])].filter((c) => match(c.name)).sort((a, b) => a.sort - b.sort || collator.compare(a.name, b.name));
    return {
      setlists: cols.filter((c) => c.kind === 'setlist'),
      folders: cols.filter((c) => c.kind === 'folder'),
      songs: [...(songsQ.data ?? [])].filter((s) => match(s.title, s.composer, s.arranger)).sort((a, b) => collator.compare(a.title, b.title)),
    };
  }, [query, colsQ.data, songsQ.data]);

  const submit = async () => {
    setError(null);
    try {
      await save.mutateAsync(items);
      toast.show(t('practice.saved'), { tone: 'ok' });
      onClose();
    } catch (e) {
      setError(errorText(t, e));
    }
  };

  const nameOf = (it: ProgramItem) =>
    it.type === 'song' ? (songs.get(it.songId)?.title ?? t('practice.unknown_song')) : (collections.get(it.collectionId)?.name ?? t('practice.unknown_list'));
  const songAdded = (id: string) => items.some((it) => it.type === 'song' && it.songId === id);
  const colAdded = (id: string) => items.some((it) => it.type === 'collection' && it.collectionId === id);
  const empty = results.setlists.length + results.folders.length + results.songs.length === 0;

  return (
    <Sheet
      open
      wide
      onClose={onClose}
      title={t('practice.songs_title')}
      footer={
        <div className="flex w-full gap-3">
          <Button variant="secondary" block onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button block busy={save.isPending} disabled={sameProgram(items, saved)} onClick={() => void submit()}>
            {t('common.save')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        <section className="flex flex-col gap-2" aria-labelledby="program-on">
          <h3 id="program-on" className="text-[17px] font-bold">
            {t('practice.on_program')} <span className="font-normal text-[var(--muted)]">({items.length})</span>
          </h3>
          {items.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-[var(--line)] px-4 py-5 text-center text-[var(--muted)]">{t('practice.on_program_empty')}</p>
          ) : (
            <ol className="flex flex-col gap-1">
              {items.map((it, i) => {
                const name = nameOf(it);
                const col = it.type === 'collection' ? collections.get(it.collectionId) : undefined;
                const song = it.type === 'song' ? songs.get(it.songId) : undefined;
                const Icon: LucideIcon = it.type === 'song' ? Music : col?.kind === 'folder' ? Folder : ListOrdered;
                const sub = col ? t('practice.whole_list', { count: t('practice.count', { count: col.entries.length }) }) : song ? creditsLine(t, song) : null;
                return (
                  <li key={it.id} className="flex min-h-14 items-center gap-2 rounded-2xl bg-[var(--surface-2)] py-1 pr-1 pl-3">
                    <Icon className="size-5 shrink-0 text-[var(--muted)]" aria-hidden />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-semibold">{name}</span>
                      {sub ? <span className="block truncate text-[14px] text-[var(--muted)]">{sub}</span> : null}
                    </span>
                    <RowButton icon={ArrowUp} label={t('practice.up_aria', { name })} disabled={i === 0} onClick={() => setItems(moveItem(items, i, -1))} />
                    <RowButton icon={ArrowDown} label={t('practice.down_aria', { name })} disabled={i === items.length - 1} onClick={() => setItems(moveItem(items, i, 1))} />
                    <RowButton icon={X} label={t('practice.remove_aria', { name })} danger onClick={() => setItems(removeItem(items, it.id))} />
                  </li>
                );
              })}
            </ol>
          )}
          {full ? <p className="text-[14px] text-[var(--muted)]">{t('practice.max_items', { max: MAX_PROGRAM_ITEMS })}</p> : null}
        </section>

        <section className="flex flex-col gap-3" aria-labelledby="program-add">
          <h3 id="program-add" className="text-[17px] font-bold">
            {t('practice.add_title')}
          </h3>
          <SearchField value={query} onChange={setQuery} placeholder={t('practice.search')} />
          {empty ? <p className="px-1 text-[var(--muted)]">{t('practice.no_results')}</p> : null}
          <ResultGroup title={t('practice.setlists')}>
            {results.setlists.map((c) => (
              <AddRow key={c.id} icon={ListOrdered} name={c.name} sub={t('practice.count', { count: c.entries.length })} added={colAdded(c.id)} full={full} onAdd={() => setItems(addCollection(items, c.id, uuid()))} />
            ))}
          </ResultGroup>
          <ResultGroup title={t('practice.folders')}>
            {results.folders.map((c) => (
              <AddRow key={c.id} icon={Folder} name={c.name} sub={t('practice.count', { count: c.entries.length })} added={colAdded(c.id)} full={full} onAdd={() => setItems(addCollection(items, c.id, uuid()))} />
            ))}
          </ResultGroup>
          <ResultGroup title={t('practice.songs')}>
            {results.songs.map((s) => (
              <AddRow key={s.id} icon={Music} name={s.title} sub={songSub(t, s)} added={songAdded(s.id)} full={full} onAdd={() => setItems(addSong(items, s.id, uuid()))} />
            ))}
          </ResultGroup>
        </section>
        {error ? <ErrorBox>{error}</ErrorBox> : null}
      </div>
    </Sheet>
  );
}

function songSub(t: ReturnType<typeof useT>, s: SongListItemDTO): string | null {
  return s.composer || s.arranger ? creditsLine(t, s) : null;
}

function ResultGroup({ title, children }: { title: string; children: ReactNode[] }) {
  if (children.length === 0) return null;
  return (
    <div className="flex flex-col gap-1">
      <h4 className="px-1 text-[14px] font-semibold tracking-wide text-[var(--muted)] uppercase">{title}</h4>
      <ul className="flex flex-col gap-1">{children}</ul>
    </div>
  );
}

function AddRow({ icon: Icon, name, sub, added, full, onAdd }: { icon: LucideIcon; name: string; sub: string | null; added: boolean; full: boolean; onAdd: () => void }) {
  const t = useT();
  return (
    <li>
      <button
        type="button"
        onClick={onAdd}
        disabled={added || full}
        aria-label={added ? t('practice.added_aria', { name }) : t('practice.add_aria', { name })}
        className="flex min-h-14 w-full items-center gap-3 rounded-2xl px-3 text-left transition hover:bg-[var(--accent-soft)] disabled:cursor-default disabled:hover:bg-transparent"
      >
        <Icon className="size-5 shrink-0 text-[var(--muted)]" aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="block truncate font-semibold">{name}</span>
          {sub ? <span className="block truncate text-[14px] text-[var(--muted)]">{sub}</span> : null}
        </span>
        <span
          className={cx(
            'flex size-9 shrink-0 items-center justify-center rounded-full',
            added ? 'bg-[var(--ok)] text-white' : full ? 'text-[var(--muted)] ring-1 ring-[var(--line)]' : 'bg-[var(--accent)] text-[var(--accent-ink)]',
          )}
          aria-hidden
        >
          {added ? <Check className="size-5" strokeWidth={3} /> : <Plus className="size-5" />}
        </span>
      </button>
    </li>
  );
}

function RowButton({ icon: Icon, label, disabled, danger, onClick }: { icon: LucideIcon; label: string; disabled?: boolean; danger?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={cx(
        'flex size-11 shrink-0 items-center justify-center rounded-xl transition hover:bg-[var(--surface)] disabled:opacity-30',
        danger ? 'text-[var(--danger-text)]' : 'text-[var(--ink-2)]',
      )}
    >
      <Icon className="size-5" aria-hidden />
    </button>
  );
}
